import React, { useEffect, useRef, useState } from "react";
import Matter from "matter-js";
import {
    DiJavascript1,
    DiReact,
    DiNodejs,
    DiPython,
    DiJava
} from "react-icons/di";
import {
    SiNextdotjs,
    SiCsharp,
    SiTypescript,
    SiVuedotjs, SiCplusplus, SiSupabase, SiNestjs, SiTailwindcss, SiCss3,
} from "react-icons/si";
import {FaAws} from "react-icons/fa";

export default function TechstackPhysics() {
    const sceneRef = useRef(null);
    const [bodies, setBodies] = useState([]);

    useEffect(() => {
        const { Engine, Render, Runner, World, Bodies, Events, Mouse, MouseConstraint } = Matter;

        const engine = Engine.create();
        engine.gravity.y = 1;

        const width = 700;
        const height = 600;

        const render = Render.create({
            element: sceneRef.current,
            engine: engine,
            options: {
                width,
                height,
                wireframes: false,
                background: "transparent",
            },
        });
        render.canvas.style.pointerEvents = "auto";

        // ✅ skills
        const skills = [
            { icon: <DiJavascript1 size={90} />, size: 100, name: "JavaScript" },
            { icon: <DiReact size={90} />, size: 80, name: "React" },
            { icon: <DiNodejs size={90} />, size: 80, name: "Node.js" },
            { icon: <DiPython size={90} />, size: 80, name: "Python" },
            { icon: <SiTypescript size={90} />, size: 80, name: "TypeScript" },
            { icon: <SiNextdotjs size={90} />, size: 80, name: "Next.js" },
            { icon: <SiVuedotjs size={90} />, size: 80, name: "Vue.js" },
            { icon: <DiJava size={90} />, size: 80, name: "Vue.js" },
            { icon: <SiCsharp size={90} />, size: 80, name: "Vue.js" },
            { icon: <SiCplusplus size={90} />, size: 80, name: "Vue.js" },
            { icon: <FaAws  size={90} />, size: 80, name: "Vue.js" },
            { icon: <SiSupabase  size={90} />, size: 80, name: "Vue.js" },
            { icon: <SiNestjs size={90} />, size: 80 },
            { icon: <SiTailwindcss size={90} />, size: 80 },
            { icon: <SiCss3 size={90} />, size: 80 },
        ];

        // ✅ create circles
        const skillBodies = []
        skills.forEach((skill, i) => {
            setTimeout(() => {
                const body = Bodies.circle(
                    width / 2, // ✅ center X
                    -300,       // ✅ start above view
                    70,
                    {
                        restitution: 0.6,      // less bouncy
                        friction: 0.1,         // moderate friction to slow sliding
                        frictionStatic: 0.3,   // makes bodies settle quickly
                        frictionAir: 0.02,     // small air resistance
                        density: 0.002,        // moderate mass
                        slop: 0.01,             // collision tolerance
                        render: {
                            fillStyle: "transparent",
                            strokeStyle: "white",
                            lineWidth: 4,
                        },
                    }
                );
                body.skill = skill;
                skillBodies.push(body);
                World.add(engine.world, body);
            }, i * 300); // ⏱️ 0.5s delay between each
        });

        // ✅ walls (box)
        const thickness = 20;

        const floor = Bodies.rectangle(width / 2, height - 10, width, 20, {
            isStatic: true,
            render: { fillStyle: "white" },
        });

        const leftWall = Bodies.rectangle(
            thickness / 2,
            height / 2,
            thickness,
            height,
            {
                isStatic: true,
                render: { fillStyle: "white" },
                chamfer: { radius: 10 }
            }
        );

        const rightWall = Bodies.rectangle(
            width - thickness / 2,
            height / 2,
            thickness,
            height,
            {
                isStatic: true,
                render: { fillStyle: "white" },
                chamfer: { radius: 10  }
            }
        );

        const mouse = Mouse.create(render.canvas);

        const mouseConstraint = MouseConstraint.create(engine, {
            mouse: mouse,
            constraint: {
                stiffness: 0.2, // how "tight" the drag feels
                render: {
                    visible: false, // hide line
                },
            },
        });

        // World.add(engine.world, mouseConstraint);

// keep mouse in sync with renderer
        World.add(engine.world, [...skillBodies, floor, leftWall, rightWall, mouseConstraint]);

        render.mouse = mouse;
        Render.run(render);

        const runner = Runner.create();
        Runner.run(runner, engine);

        // ✅ sync physics → React
        Events.on(engine, "afterUpdate", () => {
            setBodies([...skillBodies]);
        });

        return () => {
            Render.stop(render);
            Runner.stop(runner);
            Engine.clear(engine);
            render.canvas.remove();
        };
    }, []);

    return (
        <div style={{ justifyContent: "center", display: "flex" }}>
            <div
                ref={sceneRef}
                style={{ position: "relative", width: "700px", height: "600px", display: "flex", justifyContent: "center", overflow: "hidden" }}
            >
                {/* ✅ ICONS OVERLAY */}
                {bodies.map((body, i) => (
                    <div
                        key={i}
                        style={{
                            position: "absolute",
                            left: body.position.x,
                            top: body.position.y,
                            transform: `translate(-50%, -50%) rotate(${body.angle}rad)`, // ✅ rotation
                            width: body.skill.size,
                            height: body.skill.size,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {/* The icon */}
                        {body.skill.icon}

                        {/* Tooltip */}
                        <div
                            style={{
                                position: "absolute",
                                bottom: "110%", // above the icon
                                left: "50%",
                                transform: "translateX(-50%)",
                                padding: "4px 8px",
                                background: "rgba(0,0,0,0.7)",
                                color: "white",
                                fontSize: "12px",
                                borderRadius: "4px",
                                whiteSpace: "nowrap",
                                opacity: 0,
                                pointerEvents: "none",
                                transition: "opacity 0.2s",
                            }}
                            className="tooltip"
                        >
                            {body.skill.name}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}